/**
 * The CV step — `POST /api/onboarding/cv` (multipart) to upload one, and
 * `GET /api/onboarding/cv` to find the one already on file (PIVOT-085).
 *
 * Two ways out of this step: send a PDF, or continue with the stored CV. The
 * stored CV leads, because a returning candidate being made to hunt down their
 * CV again is the whole complaint this exists to answer — the file *and* the
 * analysis are already on the server, so neither the upload nor the parse needs
 * repeating. Uploading is still one click away, for a CV that has moved on.
 *
 * **The client checks are a courtesy, not a gate.** The route validates the
 * upload itself and rejects with `413` (over 10 MB) or `415` (the leading bytes
 * are not `%PDF-`, or the part was empty). The 415 in particular fires on files
 * that pass everything checkable here: a `.docx` renamed to `.pdf` has the right
 * extension *and* the browser labels it `application/pdf`, and only the server
 * reads the magic bytes. So both statuses get their own message — "something
 * went wrong" tells a candidate with a 40 MB scan nothing they can act on.
 *
 * A mobile browser can end the request while the DeepSeek parse still runs.
 * The server can then save the CV after the client reports failure. Recovery
 * polls the saved CV until Cloud Run's request deadline, and accepts only a
 * new upload timestamp. This prevents a second paid parse and never mistakes
 * the old CV for a successful replacement (2026-08-21 incident). SQL can use
 * a legacy profile timestamp when `cv_uploaded_at` is null; a new save always
 * stamps `cv_uploaded_at`, so that fallback is only the stable baseline.
 */

import { useEffect, useRef, useState } from 'react'
import type { DragEvent, FormEvent } from 'react'
import { getSavedCv, uploadCv } from '../api/onboarding'
import { ApiError, isAbortError, withRequestTimeout } from '../api/client'
import { useApiResource } from '../hooks/useApiResource'
import { useI18n } from '../i18n'
import { Icon } from './Icon'
import { Thinking } from './Thinking'
import type { Locale } from '../types/domain'
import './CvUpload.css'

/** Mirrors `routes/onboarding.py::_MAX_CV_UPLOAD_BYTES`. Change both together. */
const MAX_UPLOAD_MB = 10
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024
/** Mirrors `infra/variables.tf::web_timeout_seconds`. Change both together. */
const UPLOAD_RECOVERY_DEADLINE_MS = 300_000
// Cloud Run can receive the request after the browser starts a large upload.
// This covers a 10 MB transfer at about 1 Mbps, plus 40 seconds of margin.
const UPLOAD_TRANSFER_MARGIN_MS = 120_000
const MAX_CONSECUTIVE_RECOVERY_READ_FAILURES = 3

// Mobile Safari ended the 2026-08-21 upload request before DeepSeek returned
// at 117 seconds. Cloud Run still saved the CV. These checks recover that
// successful result without a second paid parse.
const UPLOAD_RECOVERY_DELAYS_MS = [0, 2_000, 3_000, 5_000, 8_000, 13_000, 21_000, 30_000]
const RECOVERABLE_PROXY_STATUSES = new Set([502, 503, 504])

interface CvUploadProps {
  /** A CV is staged server-side — by this upload or by an earlier one — so the
   * interview can open. */
  onReady: () => void
}

function isPdf(file: File): boolean {
  const type = file.type.toLowerCase()
  return (
    type === 'application/pdf' ||
    file.name.toLowerCase().endsWith('.pdf') ||
    // Some phone document providers give a real file no useful MIME type.
    // The server checks `%PDF-` before any model call, so unknown metadata can
    // safely pass this courtesy check and receive the authoritative verdict.
    type === '' ||
    type === 'application/octet-stream'
  )
}

function formatUploadDate(iso: string, locale: Locale): string {
  const parsed = new Date(iso)
  // An unparseable date is not worth blocking the step over; show it raw.
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abortReason = () => signal.reason ?? new DOMException('Aborted', 'AbortError')
    if (signal.aborted) {
      reject(abortReason())
      return
    }
    const timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', abort)
      resolve()
    }, ms)
    const abort = () => {
      window.clearTimeout(timeout)
      reject(abortReason())
    }
    signal.addEventListener('abort', abort, { once: true })
  })
}

function withRecoveryReadTimeout(signal: AbortSignal): AbortSignal {
  return withRequestTimeout(signal)
}

async function recoverSavedCv(
  signal: AbortSignal,
  previousUploadedAt: string | null,
  deadlineAt: number,
): Promise<boolean> {
  let attempt = 0
  let consecutiveReadFailures = 0
  while (true) {
    const configuredDelay =
      UPLOAD_RECOVERY_DELAYS_MS[attempt] ??
      UPLOAD_RECOVERY_DELAYS_MS[UPLOAD_RECOVERY_DELAYS_MS.length - 1]
    const remainingMs = Math.max(0, deadlineAt - Date.now())
    const delayMs = Math.min(configuredDelay, remainingMs)
    await delay(delayMs, signal)
    try {
      const saved = await getSavedCv(withRecoveryReadTimeout(signal))
      consecutiveReadFailures = 0
      if (saved && saved.uploaded_at !== previousUploadedAt) return true
    } catch (reason) {
      if (isAbortError(reason)) throw reason
      if (reason instanceof ApiError && reason.status < 500) return false
      consecutiveReadFailures += 1
      if (consecutiveReadFailures >= MAX_CONSECUTIVE_RECOVERY_READ_FAILURES) return false
      // A temporary read failure does not prove that the original parse failed.
    }
    if (Date.now() >= deadlineAt) return false
    attempt += 1
  }
}

function canRecoverUpload(reason: unknown): boolean {
  if (reason instanceof ApiError) return RECOVERABLE_PROXY_STATUSES.has(reason.status)
  return true
}

export function CvUpload({ onReady }: CvUploadProps) {
  const { t, locale } = useI18n()
  const savedCv = useApiResource(getSavedCv)
  const [replacing, setReplacing] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const recoveryRef = useRef<AbortController | null>(null)

  useEffect(
    () => () => {
      recoveryRef.current?.abort()
    },
    [],
  )

  const selectFile = (next: File | null | undefined) => {
    if (!next) return
    if (!isPdf(next)) {
      setError(t('onboarding.uploadInvalidType'))
      return
    }
    // Caught here so a 40 MB scan is refused instantly instead of after the
    // whole upload. The server enforces the same cap; this only saves the wait.
    if (next.size > MAX_UPLOAD_BYTES) {
      setError(t('onboarding.uploadTooLarge', { limit: MAX_UPLOAD_MB }))
      return
    }
    setError(null)
    setFile(next)
  }

  const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    setDragging(false)
    if (uploading) return
    selectFile(e.dataTransfer.files?.[0])
  }

  const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    if (!uploading) setDragging(true)
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!file || uploading) return
    recoveryRef.current?.abort()
    const controller = new AbortController()
    recoveryRef.current = controller
    const startedAt = Date.now()
    const previousUploadedAt = savedCv.data?.uploaded_at ?? null
    const recoveryBaselineKnown = !savedCv.failed
    setUploading(true)
    setError(null)
    try {
      await uploadCv(file)
      if (!controller.signal.aborted) onReady()
    } catch (reason) {
      if (controller.signal.aborted) return
      // Both refusals are fixable by picking a different file, so say which one
      // it was. Anything else really is "something went wrong".
      if (reason instanceof ApiError && reason.status === 413) {
        setError(t('onboarding.uploadTooLarge', { limit: MAX_UPLOAD_MB }))
      } else if (reason instanceof ApiError && reason.status === 415) {
        setError(t('onboarding.uploadRejectedType'))
      } else if (isAbortError(reason)) {
        return
      } else if (recoveryBaselineKnown && canRecoverUpload(reason)) {
        try {
          if (
            await recoverSavedCv(
              controller.signal,
              previousUploadedAt,
              startedAt + UPLOAD_RECOVERY_DEADLINE_MS + UPLOAD_TRANSFER_MARGIN_MS,
            )
          ) {
            onReady()
          } else setError(t('common.error'))
        } catch (recoveryReason) {
          if (!isAbortError(recoveryReason)) setError(t('common.error'))
        }
      } else {
        setError(t('common.error'))
      }
    } finally {
      if (recoveryRef.current === controller) recoveryRef.current = null
      setUploading(false)
    }
  }

  // `data` is `null` both before the first load and when there is no stored CV,
  // so the loading flag is what tells them apart. Worth waiting for: flashing
  // the dropzone and then replacing it with "we already have your CV" reads as
  // a glitch, and the call is a single indexed row.
  if (savedCv.loading) {
    return (
      <div className="stack">
        <h2>{t('onboarding.uploadTitle')}</h2>
        <p role="status">{t('common.loading')}</p>
      </div>
    )
  }

  // A failed lookup falls through to the upload form rather than showing an
  // error with a retry: "is there a CV on file" is an optimization, and the
  // step is still completable without the answer. Blocking onboarding on it
  // would trade a small annoyance for a dead end.
  const saved = savedCv.data

  if (saved && !replacing) {
    return (
      <div className="stack">
        <h2>{t('onboarding.uploadTitle')}</h2>
        <p>{t('onboarding.savedCvBody')}</p>
        <div className="cv-upload-saved">
          <p className="eyebrow">{t('onboarding.savedCvEyebrow')}</p>
          <p className="cv-upload-saved-name">{saved.filename}</p>
          <p className="cv-upload-saved-meta">
            {t('onboarding.savedCvUploadedOn', {
              date: formatUploadDate(saved.uploaded_at, locale),
            })}
          </p>
          {saved.analysis_summary && (
            <>
              <p className="cv-upload-saved-label">{t('onboarding.savedCvAnalysisLabel')}</p>
              <p className="cv-upload-saved-summary">{saved.analysis_summary}</p>
            </>
          )}
        </div>
        <button type="button" className="btn btn-primary" onClick={onReady}>
          {t('onboarding.savedCvUse')}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => setReplacing(true)}>
          {t('onboarding.savedCvReplace')}
        </button>
      </div>
    )
  }

  return (
    <form className="stack" onSubmit={handleSubmit}>
      <h2>{t('onboarding.uploadTitle')}</h2>
      <p>{t('onboarding.uploadBody')}</p>
      <label
        className={`cv-upload-dropzone${dragging ? ' cv-upload-dropzone--dragging' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".pdf,application/pdf"
          disabled={uploading}
          onChange={(e) => selectFile(e.target.files?.[0])}
        />
        <Icon name="upload" className="cv-upload-dropzone-icon" />
        <span className={file ? undefined : 'cv-upload-dropzone-cta'}>
          {file ? `${t('onboarding.uploadedAs')} ${file.name}` : t('onboarding.uploadCta')}
        </span>
        {/* PIVOT-143: names the format and the size cap the server enforces
            (`_MAX_CV_UPLOAD_BYTES`), so the boundary isn't the only thing
            marking this a control — until now nothing on the box said what
            it would accept. Only shown pre-selection; "Received: x.pdf" is
            confirmation, not an instruction to follow. */}
        {!file && (
          <span className="cv-upload-dropzone-hint">
            {t('onboarding.uploadFormatHint', { limit: MAX_UPLOAD_MB })}
          </span>
        )}
      </label>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {/* The label stays put while the model reads: swapping it to "Loading…"
          resized the button and dropped the one word saying what it does. The
          wait says what is happening instead. */}
      <button type="submit" className="btn btn-primary" disabled={!file || uploading}>
        {t('onboarding.uploadContinue')}
      </button>
      {uploading && (
        <Thinking wide label="onboarding.cvReading" patience="onboarding.cvReadingPatience" />
      )}
      {saved && (
        <button
          type="button"
          className="btn btn-ghost"
          disabled={uploading}
          onClick={() => setReplacing(false)}
        >
          {t('onboarding.savedCvKeep')}
        </button>
      )}
    </form>
  )
}
